import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import {
    useClientsApi,
    useClientsFamilies,
} from "features/admin/clients/useClients";

import { COLOR_VALUES } from "shared/config/colors";
import ClientContext from "features/admin/contexts/ClientContext";
import ClientDrawer from "features/admin/clients/ClientDrawer";
import DrawerContext from "shared/contexts/DrawerContext";
import { DropdownMultiSelectContext } from "shared/contexts/DropdownMultiSelectContext";
import GlobalConfig from "app/config/global";
import HStack from "shared/components/layout/stack/HStack";
import MasterDrawer from "shared/components/data-display/drawers/MasterDrawer";
import SecondaryButton from "shared/components/inputs/button/SecondaryButton";
import { ServerSidePaginationContext } from "shared/contexts/ServerSidePaginationContext";
import Table from "shared/components/data-display/tables/Table";
import TableFilters from "shared/components/data-display/tables/TableFilters";
import VStack from "shared/components/layout/stack/VStack";
import { getDropdownOptions } from "features/admin/offices/OfficesForm";
import { pagerOptions } from "shared/components/data-display/tables/Pager";
import updateColumnsShowAttribute from "shared/components/data-display/tables/updateColumnsShowAttribute";
import useAgencies from "features/admin/agencies/useAgencies";
import useOffices from "features/admin/offices/useOffices";

export const getCellWidthInPercent = (pxVal, tableWidth = 1312) => {
    let percentVal = (100 / tableWidth) * pxVal;
    percentVal = percentVal.toFixed(2);
    return `${percentVal}%`;
};

const columnNamesAccessor = {
    ID: "ClientId",
    Client: "Name",
    Short: "ShortName",
    ExtCode: "ExtAccountingCode",
    Agency: "LeadAgencyName",
    Family: "ClientFamily",
    Entity: "ClientCorporate",
    Office: "OfficeName",
    Internal: "ExcludeInternalOH",
    Admin: "IsAdmin",
    Publish: "Active",
};

const columns = [
    {
        headCellProps: {
            text: "ID",
            width: getCellWidthInPercent(80),
        },

        getBodyCellProps: (cell) => ({
            text: cell.value,
        }),
        accessor: columnNamesAccessor.ID,
    },
    {
        headCellProps: {
            text: "Name",
            minWidth: "189.33px",
        },

        accessor: columnNamesAccessor.Client,
    },
    {
        headCellProps: {
            text: "Short",
        },
        getBodyCellProps: (cell) => ({
            text: cell.value ?? "-",
        }),
        accessor: columnNamesAccessor.Short,
    },
    {
        headCellProps: {
            text: "Ext. Code",
            width: getCellWidthInPercent(110),
        },
        getBodyCellProps: (cell) => ({
            text: cell.value ?? "-",
        }),
        accessor: columnNamesAccessor.ExtCode,
    },
    {
        headCellProps: {
            text: "Agency",
            width: getCellWidthInPercent(100),
        },
        getBodyCellProps: (cell) => ({
            text: cell.value ?? "-",
        }),
        accessor: columnNamesAccessor.Agency,
    },
    {
        headCellProps: {
            text: "Family",
            sbe: "Admin",
            minWidth: "189.33px",
        },
        getBodyCellProps: (cell) => ({
            text: cell.value ?? "-",
        }),
        accessor: columnNamesAccessor.Family,
        show: true,
    },
    {
        headCellProps: {
            text: "Entity",
            minWidth: "189.33px",
        },
        getBodyCellProps: (cell) => ({
            text: cell.value ?? "-",
        }),
        accessor: columnNamesAccessor.Entity,
        show: true,
    },
    {
        headCellProps: {
            text: "Office",
            width: getCellWidthInPercent(120),
        },
        getBodyCellProps: (cell) => ({
            text: cell.value ?? "-",
        }),
        accessor: columnNamesAccessor.Office,
        show: true,
    },
    {
        headCellProps: {
            text: "Internal",
            centered: true,
            width: getCellWidthInPercent(80),
        },
        getBodyCellProps: (cell) => ({
            icon: cell.value ? "success" : "error",
        }),
        accessor: columnNamesAccessor.Internal,
        disableSortBy: true,
        show: true,
    },
    {
        headCellProps: {
            text: "Admin",
            centered: true,
            width: getCellWidthInPercent(80),
        },
        getBodyCellProps: (cell) => ({
            icon: cell.value ? "success" : "error",
        }),
        accessor: columnNamesAccessor.Admin,
        disableSortBy: true,
        show: true,
    },
    {
        headCellProps: {
            text: "Publish",
            centered: true,
            width: getCellWidthInPercent(80),
        },
        getBodyCellProps: (cell) => ({
            icon: cell.value ? "success" : "error",
        }),
        accessor: columnNamesAccessor.Publish,
        disableSortBy: true,
        show: true,
    },
    {
        headCellProps: {
            width: getCellWidthInPercent(80),
        },
        accessor: "Edit",
        disableSortBy: true,
        getBodyCellProps: (cell) => ({
            shownByRowHover: true,
            moreMenuActionProps: [
                {
                    text: "View Projects",
                    actionIcon: "link",
                    actionProps: {
                        to: `?action=viewProjects&id=${cell.row.original.ClientId}`,
                        replace: true,
                        "aria-label": "View Project",
                    },
                },
                {
                    text: "Manage",
                    actionIcon: "caret-right",
                    actionProps: {
                        to: `${GlobalConfig.routes.adminClientsLanding}/${cell.row.original.ClientId}/${GlobalConfig.routes.adminClientGroups}`,
                        replace: true,
                        "aria-label": "Manage Client",
                    },
                },
                {
                    text: "Edit",
                    actionIcon: "edit",
                    actionProps: {
                        to: `?action=edit&id=${cell.row.original.ClientId}`,
                        replace: true,
                        "aria-label": "Edit Client",
                    },
                },
            ],
        }),
    },
];

const tableCTAProps = {
    leadingIcon: "expand",
    label: "Add Client",
    to: "?action=add",
    replace: true,
};

const getRowProps = (row) => ({
    style: {
        background: row.values.Active === false ? COLOR_VALUES.Gray_9 : "",
    },
});

const DropdownNames = {
    agency: "Agency",
    family: "Family",
    office: "Office",
};

const FiltersConfig = [
    {
        name: "active",
        columnAccessor: columnNamesAccessor.ID,
        unfilteredValue: "all",
        isSegmentedControl: true,
        options: [
            {
                value: "all",
                label: "All",
            },
            {
                value: "active",
                label: "Active",
            },
            {
                value: "inactive",
                label: "Inactive",
            },
        ],
    },
    {
        showColumnFilter: true,
        name: "Columns",
    },
    {
        name: DropdownNames.agency,
        columnAccessor: columnNamesAccessor.Agency,
        unfilteredValue: "all",
        isDropdownControl: true,
        formInputWrapperClassName: "max-width-160",
        dropdownFilterLabel: DropdownNames.agency,
    },
    {
        name: DropdownNames.family,
        columnAccessor: columnNamesAccessor.Family,
        unfilteredValue: "all",
        isDropdownControl: true,
        formInputWrapperClassName: "max-width-160",
        dropdownFilterLabel: DropdownNames.family,
    },
    {
        name: DropdownNames.office,
        columnAccessor: columnNamesAccessor.Office,
        unfilteredValue: "all",
        isDropdownControl: true,
        formInputWrapperClassName: "max-width-160",
        dropdownFilterLabel: DropdownNames.office,
    },
];

const multiSelectDropownOptionsArr = [
    { parent: "Main", children: ["Family", "Entity", "Office"] },
    { parent: "Attributes", children: ["Internal", "Admin", "Publish"] },
];

const ClientsTable = () => {
    const { queryPageIndex, queryPageSize, querySortBy, totalItemsUpdate } =
        useContext(ServerSidePaginationContext);

    const { setActiveInactiveState } = useContext(ClientContext);

    const { multiSelectDropdownOptionsList, setMultiSelectDropdownOptions } =
        useContext(DropdownMultiSelectContext);

    const [filters, setFilters] = useState();
    const [filtersConfigVal, setFiltersConfigVal] = useState(FiltersConfig);

    const [drawerClientId, setDrawerClientId] = useState();
    const { setShow } = useContext(DrawerContext);
    const [columnsNew, setColumnsNew] = useState(columns);

    const { agencies } = useAgencies();
    const { clientFamilies } = useClientsFamilies();
    const { offices } = useOffices();
    const [loadingFamilyFilterOptions, setLoadingFamilyFilterOptions] =
        useState(true);

    const { useClientsList } = useClientsApi();
    const showFilterVal = useRef();

    useEffect(() => {
        setMultiSelectDropdownOptions(multiSelectDropownOptionsArr);
    }, [setMultiSelectDropdownOptions]); 

    switch (filters?.find((f) => f.id === columnNamesAccessor.ID)?.value) {
        case undefined:
            showFilterVal.current = "";
            break;
        case "active":
            showFilterVal.current = true;
            break;
        case "inactive":
            showFilterVal.current = false;
            break;
        default:
            showFilterVal.current = true;
            break;
    }

    const clientsDataApi = useClientsList({
        sortBy: querySortBy,
        pageSize: queryPageSize,
        pageIndex: queryPageIndex,
        active: showFilterVal.current,
        officeRegionId:
            filters?.find((f) => f.id === columnNamesAccessor.Office)?.value ??
            "",
        clientFamilyId:
            filters?.find((f) => f.id === columnNamesAccessor.Family)?.value ??
            "",
        leadAgencyId:
            filters?.find((f) => f.id === columnNamesAccessor.Agency)?.value ??
            "",
    });

    useEffect(() => {
        if (!clientsDataApi.isLoading) {
            totalItemsUpdate(clientsDataApi?.data?.TotalItems);
        }
    }, [clientsDataApi?.data, clientsDataApi.isLoading, totalItemsUpdate]);

    useEffect(() => {
        if (!clientsDataApi.isLoading) {
            setActiveInactiveState(showFilterVal.current);
        }
    }, [clientsDataApi?.data, clientsDataApi.isLoading, setActiveInactiveState]);

    const handleDrawerClick = useCallback((clientId) => {
        setShow(true);
        setDrawerClientId(clientId);
    }, [setShow]);

    const handleDrawerClose = useCallback(() => {
        setShow(false);
        setDrawerClientId(null);
    }, [setShow]);

    const getNameCellProps = useCallback((cell) => ({
        text: cell.value ?? "-",
        actionIcon: "caret-right",
        actionProps: {
            onClick: () => handleDrawerClick(cell.row.original.ClientId),
        },
    }), [handleDrawerClick]);

    useEffect(() => {
        const i = columns.findIndex((f) => f.headCellProps.text === "Name");
        setColumnsNew((prevColumns) => {
            const newColArr = [...prevColumns];
            newColArr[i].getBodyCellProps = getNameCellProps;
            return newColArr;
        });
    }, [getNameCellProps]);

    useEffect(() => {
        if (!agencies.isLoading && agencies?.data?.Items) {
            setFiltersConfigVal((prevFilters) => {
                const agencyIndex = prevFilters.findIndex(
                    (f) => f.name === DropdownNames.agency
                );
                const newOptions = getDropdownOptions(
                    agencies.data.Items,
                    "Id",
                    "Name",
                    "all",
                    "All"
                );
                
                if (newOptions.length === 0 || 
                    JSON.stringify(prevFilters[agencyIndex]?.options) === JSON.stringify(newOptions)) {
                    return prevFilters;
                }
                
                let newObj = [...prevFilters];
                newObj[agencyIndex].options = newOptions;
                return newObj;
            });
        }
    }, [agencies.isLoading, agencies?.data?.Items]);

    useEffect(() => {
        if (!clientFamilies.isLoading && clientFamilies?.data) {
            const newOptions = getDropdownOptions(
                clientFamilies.data,
                "clientFamilyID",
                "Name",
                "all",
                "All"
            );
            
            setFiltersConfigVal((prevFilters) => {
                const familyIndex = prevFilters.findIndex(
                    (f) => f.name === DropdownNames.family
                );
                
                if (JSON.stringify(prevFilters[familyIndex]?.options) === JSON.stringify(newOptions)) {
                    return prevFilters;
                }
                
                let newObj = [...prevFilters];
                newObj[familyIndex].options = newOptions;
                return newObj;
            });
            
            if (clientFamilies.data.length <= 0 || newOptions.length > 0) {
                setLoadingFamilyFilterOptions(false);
            }
        }
    }, [clientFamilies.isLoading, clientFamilies?.data]);

    useEffect(() => {
        if (!offices.isLoading && offices?.data) {
            setFiltersConfigVal((prevFilters) => {
                const officeIndex = prevFilters.findIndex(
                    (f) => f.name === DropdownNames.office
                );
                const newOptions = getDropdownOptions(
                    offices.data,
                    "OfficeRegionId",
                    "Name",
                    "all",
                    "All"
                );
                
                if (newOptions.length === 0 || 
                    JSON.stringify(prevFilters[officeIndex]?.options) === JSON.stringify(newOptions)) {
                    return prevFilters;
                }
                
                let newObj = [...prevFilters];
                newObj[officeIndex].options = newOptions;
                return newObj;
            });
        }
    }, [offices.isLoading, offices?.data]);

    const handleOnChangeDropdownColumn = (selectedValues) => {
        let newObj = [...columnsNew];

        updateColumnsShowAttribute(
            columns,
            multiSelectDropownOptionsArr,
            newObj,
            selectedValues
        );

        setColumnsNew(newObj);
    };

    const memoizedTable = useMemo(
        () => (
            <Table
                dataIsFetching={clientsDataApi.isFetching}
                showTableBorder={true}
                columns={columnsNew}
                data={clientsDataApi?.data?.Items}
                getRowProps={getRowProps}
                paginationFromFE={false}
                emptyActionProps={tableCTAProps}
                pagerDisabledOptionsList={[pagerOptions[0]]}
            />
        ),
        [clientsDataApi, columnsNew]
    );

    if (
        clientsDataApi.isLoading ||
        agencies.isLoading ||
        clientFamilies.isLoading ||
        offices.isLoading ||
        loadingFamilyFilterOptions
    ) {
        return <>Loading...</>;
    }

    if (
        clientsDataApi.isError ||
        agencies.isError ||
        clientFamilies.isError ||
        offices.isError
    ) {
        return <>Error</>;
    }

    return (
        <>
            {drawerClientId && (
                <MasterDrawer
                    stickyAction={true}
                    onRequestClose={handleDrawerClose}
                >
                    <ClientDrawer clientId={drawerClientId} />
                </MasterDrawer>
            )}

            <VStack>
                <HStack align="end" justify="space-between">
                    <HStack align="flex-end">
                        <TableFilters
                            initialFilterData={[
                                { name: "active", value: "active" },
                            ]}
                            filtersConfig={filtersConfigVal}
                            onChange={setFilters}
                            multiSelectDropdownOptionsList={
                                multiSelectDropdownOptionsList
                            }
                            handleOnChangeDropdownColumn={
                                handleOnChangeDropdownColumn
                            }
                        />
                    </HStack>
                    <SecondaryButton {...tableCTAProps} />
                </HStack>

                {memoizedTable}
            </VStack>
        </>
    );
};

export default React.memo(ClientsTable);
